import { useEffect } from 'react';
import { Controller, useForm, type UseFormProps } from 'react-hook-form';
import {
	Button,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Separator
} from '@/components/ui';
import { DateTimeField } from '@/components/custom';
import { ControlledInput } from '@/components/controlled';
import { DEFAULT_FORM_CONFIG } from '@/constants/formConfig.constant';
import { PROMO_CODE_MAX_PERCENT_BONUS } from '@/constants';
import { getApiErrorMessage, notify } from '@/functions';
import { useRequestPromoCodeActions } from '@/hooks/httpRequest/promoCodes';
import { CoinPackageNameEnum, PromoCodeBonusTypeEnum } from '@/enums';
import { cn } from '@/lib/utils';
import type { IPromoCodeFormData, IPromoCodeOffer, IPromoCodePayload } from '@/interfaces';
import PromoCodeAnnouncementFields from './PromoCodeAnnouncementFields';
import { Required, SectionTitle } from './PromoCodeFormParts';
import PromoCodeUserField from './PromoCodeUserField';

export type TPromoCodeFormMode = 'create' | 'edit' | 'duplicate';

type TProps = {
	open: boolean;
	mode: TPromoCodeFormMode;
	offer?: IPromoCodeOffer;
	onClose: () => void;
};

const TITLES: Record<TPromoCodeFormMode, string> = {
	create: 'Nouveau code promo',
	edit: 'Modifier le code promo',
	duplicate: 'Réutiliser avec un nouveau code',
};

const PACKS = Object.values(CoinPackageNameEnum);

const emptyForm: IPromoCodeFormData = {
	name: '',
	code: '',
	bonusType: PromoCodeBonusTypeEnum.PERCENT,
	bonusValue: '',
	packs: [],
	startsAt: undefined,
	endsAt: undefined,
	maxUses: '',
	targetEmail: '',
	notify: false,
	title: { fr: '', en: '' },
	message: { fr: '', en: '' },
};

const toFormData = (offer: IPromoCodeOffer, mode: TPromoCodeFormMode): IPromoCodeFormData => ({
	name: offer.name,
	// a reused offer needs its own code
	code: mode === 'duplicate' ? '' : offer.code,
	bonusType: offer.bonusType,
	bonusValue: offer.bonusValue,
	packs: offer.packs,
	// a reused offer picks new dates: the old ones are in the past
	startsAt: mode === 'duplicate' ? undefined : new Date(offer.startsAt),
	endsAt: mode === 'duplicate' ? undefined : new Date(offer.endsAt),
	maxUses: offer.maxUses ?? '',
	targetEmail: offer.targetEmail ?? '',
	// an announcement already sent is not sent again
	notify: !!offer.announcement && (mode === 'duplicate' || !offer.notifiedAt),
	title: offer.announcement?.title ?? { fr: '', en: '' },
	message: offer.announcement?.message ?? { fr: '', en: '' },
});

// an empty limit is no limit: on an edit it is sent as null to clear a limit the offer had
const toLimit = (value: number | '', mode: TPromoCodeFormMode) => {
	if (value !== '') return Number(value);
	// cleared on an edit or a reuse: null removes the source's limit
	return mode === 'create' ? undefined : null;
};

/* notify unticked: null turns the announcement off, except on a new offer and on one already announced (kept as a record) */
const toNoAnnouncement = (mode: TPromoCodeFormMode, offer?: IPromoCodeOffer) => {
	if (mode === 'create' || (mode === 'edit' && offer?.notifiedAt)) return undefined;
	return null;
};

const PromoCodeFormDialog = ({ open, mode, offer, onClose }: TProps) => {
	const { triggerCreate, triggerUpdate, triggerDuplicate, isLoading } = useRequestPromoCodeActions();
	const formMethods = useForm<IPromoCodeFormData>({
		...(DEFAULT_FORM_CONFIG as UseFormProps<IPromoCodeFormData>),
		defaultValues: emptyForm,
	});
	const {
		control,
		handleSubmit,
		reset,
		watch,
		setValue,
		setError,
		formState: { errors },
	} = formMethods;

	useEffect(() => {
		if (open) reset(offer ? toFormData(offer, mode) : emptyForm);
	}, [open, offer, mode, reset]);

	// the API refuses a new code or a new member once the offer was used
	const isLocked = mode === 'edit' && !!offer?.usedCount;
	const bonusType = watch('bonusType');
	const packs = watch('packs');
	const startsAt = watch('startsAt');

	const togglePack = (pack: CoinPackageNameEnum) =>
		setValue('packs', packs.includes(pack) ? packs.filter(item => item !== pack) : [...packs, pack], { shouldValidate: true });

	const onSubmit = async (form: IPromoCodeFormData) => {
		if (!form.packs.length) {
			setError('packs', { message: 'Choisis au moins un pack.' });
			return;
		}
		if (!form.startsAt || !form.endsAt) return;

		const payload: IPromoCodePayload = {
			name: form.name.trim(),
			code: form.code.trim(),
			bonusType: form.bonusType,
			bonusValue: Number(form.bonusValue),
			packs: form.packs,
			startsAt: form.startsAt.toISOString(),
			endsAt: form.endsAt.toISOString(),
			maxUses: toLimit(form.maxUses, mode),
			targetEmail: form.targetEmail.trim() || (mode === 'create' ? undefined : null),
			announcement: form.notify ? { title: form.title, message: form.message } : toNoAnnouncement(mode, offer),
		};

		try {
			if (mode === 'edit' && offer) await triggerUpdate({ id: offer._id, data: payload });
			else if (mode === 'duplicate' && offer) await triggerDuplicate({ id: offer._id, data: payload });
			else await triggerCreate(payload);
			notify(mode === 'edit' ? 'Offre mise à jour.' : 'Offre enregistrée.');
			onClose();
		} catch (error) {
			notify(getApiErrorMessage(error, "Erreur lors de l'enregistrement."), 'error');
		}
	};

	return (
		<Dialog open={open} onOpenChange={value => !value && onClose()}>
			<DialogContent className="max-w-4xl w-[calc(100%-2rem)] max-h-[96vh] gap-3 overflow-y-auto rounded-2xl scrollbar-none [&::-webkit-scrollbar]:hidden">
				<DialogHeader>
					<DialogTitle>{TITLES[mode]}</DialogTitle>
				</DialogHeader>

				<form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
					<div className="grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2">
						{/* Left: what the offer gives */}
						<div className="space-y-4">
							<div className="space-y-3">
								<SectionTitle>Offre</SectionTitle>
								<div className="flex flex-col gap-1">
									<Label>
										Nom de l'offre <Required />
									</Label>
									<ControlledInput
										name="name"
										control={control}
										placeholder="ex: Offre de rentrée"
										rules={{ required: 'Champ requis' }}
										maxLength={80}
									/>
								</div>
								<div className="flex flex-col gap-1">
									<Label>
										Code à saisir <Required />
									</Label>
									<ControlledInput
										name="code"
										control={control}
										className="font-mono uppercase"
										placeholder="ex: RENTREE20"
										rules={{
											required: 'Champ requis',
											pattern: { value: /^[A-Za-z0-9]{4,20}$/, message: '4 à 20 lettres ou chiffres' },
										}}
										maxLength={20}
										disabled={isLocked}
									/>
									{isLocked && <p className="text-xs text-muted-foreground">Code et compte verrouillés : l'offre a déjà été utilisée.</p>}
								</div>
								<div className="flex flex-col gap-1">
									<Label>Réservé à un compte</Label>
									<Controller
										name="targetEmail"
										control={control}
										render={({ field }) => (
											<PromoCodeUserField value={field.value} onChange={field.onChange} disabled={isLocked} />
										)}
									/>
								</div>
							</div>

							<Separator />

							<div className="space-y-3">
								<SectionTitle>Bonus</SectionTitle>
								<div className="grid grid-cols-2 gap-3">
									<div className="flex flex-col gap-1">
										<Label>Type de bonus</Label>
										<Controller
											name="bonusType"
											control={control}
											render={({ field }) => (
												<Select value={field.value} onValueChange={field.onChange}>
													<SelectTrigger className="text-xs">
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value={PromoCodeBonusTypeEnum.PERCENT}>Pourcentage du pack</SelectItem>
														<SelectItem value={PromoCodeBonusTypeEnum.FIXED}>Nombre de pièces</SelectItem>
													</SelectContent>
												</Select>
											)}
										/>
									</div>
									<div className="flex flex-col gap-1">
										<Label>
											{bonusType === PromoCodeBonusTypeEnum.PERCENT ? 'Bonus (%)' : 'Bonus (pièces)'} <Required />
										</Label>
										<ControlledInput
											name="bonusValue"
											control={control}
											type="number"
											min={1}
											placeholder={bonusType === PromoCodeBonusTypeEnum.PERCENT ? 'ex: 20' : 'ex: 100'}
											max={bonusType === PromoCodeBonusTypeEnum.PERCENT ? PROMO_CODE_MAX_PERCENT_BONUS : undefined}
											rules={{
												required: 'Champ requis',
												min: { value: 1, message: 'Minimum 1' },
												validate: value =>
													bonusType !== PromoCodeBonusTypeEnum.PERCENT ||
													Number(value) <= PROMO_CODE_MAX_PERCENT_BONUS ||
													`Maximum ${PROMO_CODE_MAX_PERCENT_BONUS} %`,
											}}
										/>
									</div>
								</div>

								<div className="flex flex-col gap-2">
									<Label>
										Packs concernés <Required />
									</Label>
									<div className="flex flex-wrap gap-2">
										{PACKS.map(pack => (
											<Button
												key={pack}
												type="button"
												size="sm"
												variant={packs.includes(pack) ? 'default' : 'outline'}
												className={cn('h-8 px-3 text-xs', errors.packs && !packs.length && 'border-red-500')}
												onClick={() => togglePack(pack)}
											>
												{pack}
											</Button>
										))}
									</div>
									{errors.packs && <span className="text-xs text-red-500">{errors.packs.message}</span>}
								</div>
							</div>
						</div>

						{/* Right: when and how often */}
						<div className="space-y-4">
							<div className="space-y-3">
								<SectionTitle>Période</SectionTitle>
								<DateTimeField name="startsAt" label="Début" placeholder="Date de début" control={control} />
								<DateTimeField
									name="endsAt"
									label="Fin"
									defaultTime={{ hours: 23, minutes: 59 }}
									placeholder="Date de fin"
									control={control}
									minDate={startsAt}
									validate={value => !startsAt || !value || value > startsAt || 'Doit être après la date de début'}
								/>
							</div>

							<Separator />

							<div className="space-y-3">
								<SectionTitle>Limites</SectionTitle>
								<div className="flex flex-col gap-1">
									<Label>Max global</Label>
									<ControlledInput name="maxUses" control={control} type="number" min={1} placeholder="Illimité" />
								</div>
								<p className="text-xs text-muted-foreground">Chaque membre ne peut l'utiliser qu'une seule fois.</p>
							</div>
						</div>
					</div>

					{/* Announcement, full width */}
					<PromoCodeAnnouncementFields form={formMethods} />

					<div className="flex justify-end gap-2 border-t pt-3">
						<Button type="button" variant="outline" onClick={onClose}>
							Annuler
						</Button>
						<Button type="submit" disabled={isLoading}>
							{isLoading ? 'Enregistrement...' : 'Enregistrer'}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default PromoCodeFormDialog;
